<svelte:options
  customElement={{
    tag: 'dynamic-svelte-events-schedule',
    props: {
      eventsId: { type: 'String' },
      baseApiUrl: { type: 'String' },
      apiUrl: { type: 'String' },
      language: { type: 'String' },
      hasFilters: { type: 'Boolean' },
      dictionary: { type: 'Object' },
      sports: { type: 'Array' },
      communities: { type: 'Array' },
    },
    shadow: 'none',
  }}
/>

<script>
  /* eslint-disable compat/compat */

  import { SvelteDate, SvelteURLSearchParams } from 'svelte/reactivity';
  import Icon from '../icon/icon.svelte';

  let {
    eventsId,
    language = 'en-US',
    dictionary,
    baseApiUrl = '',
    sports = [],
    communities = [],
    hasFilters = false,
  } = $props();

  let apiUrl = $derived(baseApiUrl + '/api/events/' + eventsId);

  let urlParams = new SvelteURLSearchParams(window.location.search);

  let date = urlParams.has('month')
    ? // Ensure date is parsed at the middle of the month
      new SvelteDate(urlParams.get('month') + '-15')
    : new SvelteDate();

  if (Number.isNaN(date.valueOf())) {
    date = new SvelteDate();
  }

  let formattedDate = $derived(
    date.toLocaleDateString(language, { year: 'numeric', month: 'long' })
  );

  let queryDate = $derived.by(() => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  let listingItems = $state([]);
  let formattedItems = $derived(
    listingItems.map((item) => ({
      ...item,
      day: new Date(item.date).toLocaleDateString(language, {
        month: 'short',
        day: '2-digit',
      }),
      year: new Date(item.date).getFullYear(),
      time: new Date(item.date).toLocaleTimeString(language, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    }))
  );

  let finishedInitialSearch = $state(false);

  async function fetchEventsSchedule() {
    urlParams.set('month', queryDate);
    const newPath = '?' + urlParams.toString();
    history.pushState({}, '', newPath);

    const params = getParams();
    let url = `${apiUrl}?${params.toString()}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      finishedInitialSearch = true;

      listingItems = data.eventsSchedule;
    } catch (error) {
      console.error('Fetch error:', error);
    }
  }

  function getParams() {
    const params = new SvelteURLSearchParams();

    params.append('month', queryDate); // Versive add current language
    params.append('lang', language); // Versive add current language

    return params;
  }

  function adjustMonth(direction) {
    // set roughly to the middle of the month so as not to skip any of the shorter months when adjusting the current month
    // (e.g., August -> October, January -> March)
    date.setDate(15);

    // Adjust the month based on the direction
    date.setMonth(date.getMonth() + direction);

    fetchEventsSchedule();
  }

  fetchEventsSchedule();
</script>

<div class="events-schedule__content">
  <!-- Begin Filter/Month Controls -->
  <div class="events-schedule__filters">
    <div class="events-schedule__filters-controls">
      <div class="events-schedule__month-picker">
        <button
          class="events-schedule__arrow-button events-schedule__arrow-button--back"
          title={dictionary.MonthPrevious}
          data-events-schedule-previous-month
          onclick={() => adjustMonth(-1)}
        >
          <Icon class="icon" width="16" height="18" icon="arrow-right" />
        </button>

        <span
          class="events-schedule__current-month"
          data-events-schedule-month-display
        >
          {formattedDate}
        </span>

        <button
          class="events-schedule__arrow-button events-schedule__arrow-button--forward"
          title={dictionary.MonthNext}
          data-events-schedule-next-month
          onclick={() => adjustMonth(1)}
        >
          <Icon class="icon" width="16" height="18" icon="arrow-right" />
        </button>
        <input
          class="events-schedule__current-month-input hidden"
          type="month"
          id="month-picker"
          name="month"
          data-events-schedule-filter
          data-events-schedule-filter-month
        />
      </div>

      {#if hasFilters}
        <button
          class="events-schedule__filters-toggle button button__secondary button__medium"
          data-events-schedule-filters-toggle
        >
          <span class="events-schedule__filters-toggle-content"
            >{dictionary.Filters}</span
          >
        </button>
      {/if}
    </div>

    <!-- TODO: Remove if filters unwanted, or finish implementing. This was
    half-implemented but currently out of scope of current fix task. -->

    {#if hasFilters}
      <div
        class="events-schedule__filters-dropdowns"
        data-events-schedule-filters-dropdown-container
      >
        <div class="events-schedule__block">
          <div class="select-wrapper">
            <select
              id="filter-event-location"
              defaultOptionTitle={dictionary.AllEventLocations}
              aria-label="Locations"
              class="events-schedule__dropdown select"
              name="location"
              data-events-schedule-filter
            >
              <option value="">{dictionary.AllEventLocations}</option>

              {#each communities as community (community.value)}
                <option value={community.value} selected={community.selected}>
                  {community.name}
                </option>
              {/each}
            </select>
          </div>
        </div>

        <div class="events-schedule__block">
          <div class="select-wrapper">
            <select
              id="filter-event-sport"
              defaultOptionTitle={dictionary.AllSportTypes}
              aria-label="Sports"
              class="events-schedule__dropdown select"
              name="sport"
              data-events-schedule-filter
            >
              <option value="">{dictionary.AllSportTypes}</option>

              @foreach (var sport in catSportChildren)
              {#each sports as sport (sport.value)}
                <option value={sport.value} selected={sport.selected}>
                  {sport.name}
                </option>
              {/each}
            </select>
          </div>
        </div>
      </div>
    {/if}
  </div>

  <ul class="events-schedule__list" data-events-schedule-listing aria-live="polite">
    {#if finishedInitialSearch}
      {#if listingItems.length == 0}
        <div
          class="no-result search-listing__no-results"
          style="padding: 50px;min-height:150px;text-align:center;"
        >
          {dictionary.NoResults}
        </div>
      {:else}
        {#each formattedItems as event (event.link)}
          <li class="events-schedule__event">
            <div class="simple-event-list-item">
              <div class="date-container">
                <div class="date-icon">
                  <Icon class="icon" icon="calendar" />
                </div>
                <time class="event-date">
                  <span>{event.day}</span>
                  {event.year}
                </time>
              </div>
              <div class="event-content">
                <div class="event-text">
                  <h3 class="event-title">
                    <a class="link-area" href={event.link}>{event.title}</a>
                  </h3>
                  <p class="event-description">
                    <!-- TODO: this is like Html.Raw; fix/sanitize -->
                    {@html event.description}
                  </p>
                  {#if event.location != null && event.location.name.length > 0}
                    <div class="detail-row location">
                      <Icon class="icon location-icon" icon="location" />

                      {#if event.location.link}
                        <a href={event.location.link}>{event.location.name}</a>
                      {:else}
                        <span>{event.location.name}</span>
                      {/if}
                    </div>
                  {/if}
                  <time class="detail-row event-time">
                    <Icon class="icon" icon="clock" />

                    <span>{event.time}</span>
                  </time>
                </div>
                {#if event.image.url.length > 0}
                  <picture class="image-wrapper">
                    <img src={event.image.url} alt={event.image.alt} />
                  </picture>
                {/if}
              </div>
            </div>
          </li>
        {/each}
      {/if}
    {/if}
  </ul>
</div>
